import { z } from 'zod'
import { REFUND_WINDOW_MS } from '../refund-window'

// What the buyer's Orders screen renders, shared by the server loader
// (`./orders.ts`) and the client panel in feature-components. Client-safe: no
// server imports, so the panel can take these types and helpers directly.

// The union of `enrollment.payment_status` and `event_registration.payment_status`,
// as displayed: a failed purchase the buyer walked away from is `abandoned`
// (`displayedPaymentStatus` in data-access, applied by the loader).
const orderStatusSchema = z.enum(['pending', 'completed', 'refunded', 'failed', 'abandoned', 'cancelled'])
export type OrderStatus = z.infer<typeof orderStatusSchema>

/** Both purchase tables constrain the column; a value outside the set is a schema change this screen missed. */
export function orderStatus(raw: string): OrderStatus {
  return orderStatusSchema.parse(raw)
}

interface OrderCommon {
  /** The purchase row id: `enrollment.id` or `event_registration.id`. */
  id: string
  title: string
  imageUrl: string | null
  pricePaidCents: number
  paymentStatus: OrderStatus
  createdAt: string
  completedAt: string | null
}

export interface CourseOrder extends OrderCommon {
  kind: 'course'
  instructorName: string | null
  refundedAt: string | null
  refundReason: string | null
}

export interface EventOrder extends OrderCommon {
  kind: 'event'
  tierName: string
  eventStartAt: string
  /** Set only for a completed registration — nothing else admits anyone. */
  ticketPath: string | null
}

export type OrderItem = CourseOrder | EventOrder

export function ticketPath(registrationId: string): string {
  return `/tickets/${encodeURIComponent(registrationId)}`
}

export function newestFirst(a: { createdAt: string }, b: { createdAt: string }): number {
  return b.createdAt.localeCompare(a.createdAt)
}

export function refundDeadline(order: CourseOrder): Date | null {
  if (!order.completedAt)
    return null
  return new Date(new Date(order.completedAt).getTime() + REFUND_WINDOW_MS)
}

/**
 * Whether to offer the cancel button. Display only — the purchases module
 * re-decides on every request. Courses only: a buyer cannot refund an event
 * ticket themselves (a Platform Admin can).
 */
export function isRefundEligible(order: CourseOrder, now: Date): boolean {
  if (order.paymentStatus !== 'completed')
    return false
  const deadline = refundDeadline(order)
  return deadline !== null && deadline.getTime() > now.getTime()
}
